/* eslint-disable max-classes-per-file */
import { AxiosError } from 'axios';

enum HttpStatus {
  BAD_REQUEST = 400,
  OK = 200,
  MOVED_PERMANENTLY = 301,
}

export class ErrorWithStatus extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export class ErrorWithJson extends Error {
  status: number;

  data: any;

  constructor(
    message: string,
    data: any,
    status: number = HttpStatus.BAD_REQUEST,
  ) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

export type Message = any;
const stringifyMessage = (message: Message) =>
  typeof message === 'string'
    ? JSON.stringify({ message })
    : JSON.stringify(message);

const corsHeaders = {
  'Access-Control-Allow-Origin': '*',
  'Access-Control-Allow-Headers': '*',
  'Access-Control-Allow-Credentials': true,
  'Access-Control-Expose-Headers': 'Content-Disposition',
};

const withCors = (headers: Record<string, string>) => ({
  ...headers,
  ...corsHeaders,
});
const isAxiosError = (error: any): error is AxiosError =>
  error?.isAxiosError === true;

export default {
  success: <T extends Message = Message>(
    message?: T,
    config?: { headers?: Record<string, string> },
  ) => {
    const response: AWSLambda.APIGatewayProxyResult = {
      statusCode: HttpStatus.OK,
      body: stringifyMessage(message || 'OK'),
    };

    response.headers = withCors(config?.headers || {});

    return response;
  },
  redirect: (location: string) => {
    const response: AWSLambda.APIGatewayProxyResult = {
      statusCode: HttpStatus.MOVED_PERMANENTLY,
      body: '',
    };

    response.headers = withCors({
      Location: location,
    });

    return response;
  },
  error: (
    error: Message | Error | ErrorWithStatus | ErrorWithJson | AxiosError,
    statusCode = HttpStatus.BAD_REQUEST,
  ) => {
    console.error(error);

    const headers = withCors({});

    if (error instanceof ErrorWithJson) {
      return {
        statusCode: error.status,
        body: JSON.stringify({ message: error.message, data: error.data }),
        headers,
      };
    }

    if (error instanceof ErrorWithStatus) {
      return {
        statusCode: error.status,
        body: stringifyMessage(error.message),
        headers,
      };
    }

    if (!(error instanceof Error))
      return { statusCode, body: stringifyMessage(error), headers };

    if (isAxiosError(error)) {
      return {
        statusCode,
        body: stringifyMessage(error?.response?.data),
        headers,
      };
    }

    return { statusCode, body: stringifyMessage(error.message), headers };
  },
  html: (body: string) => ({
    statusCode: HttpStatus.OK,
    body,
    headers: { 'Content-Type': 'text/html' },
  }),
  disposeCsv: (fileName: string, body: string) => ({
    headers: withCors({
      'Content-Type': 'text/csv',
      'Content-Disposition': `attachment; filename=${fileName}`,
    }),
    statusCode: HttpStatus.OK,
    body,
  }),
};
